/**
 * Rejection Service
 * 
 * Centralized service for managing timesheet rejection tracking.
 * Handles rejection records, status updates, and employee-specific queries.
 * Supports both demo mode (localStorage) and live mode (API).
 */

import type { RejectionRecord, RejectionStatus, RejectedEntry, RejectionSummary, TimesheetRejectionHistory } from '@/types/rejection';
import type { Timesheet, TimesheetEntry, Project, CostCentre } from '@/types/timesheet';
import { apiClient } from './apiClient';

// Backend API response type (simpler structure without affectedEntries)
interface ApiRejectionRecord {
  id: string;
  timesheetId: string;
  rejectionDate: string;
  rejectionReason: string;
  approverId: string;
  approverName: string;
  approverRole: string;
  status: RejectionStatus;
  resubmittedAt: string | null;
  resolvedAt: string | null;
  employeeNotified: boolean;
  notificationSentAt: string | null;
}

export class RejectionService {
  private static instance: RejectionService;
  private readonly STORAGE_KEY = 'timesheet_rejections';
  private isLiveMode: boolean = false;
  
  private constructor() {}
  
  /**
   * Get singleton instance
   */
  static getInstance(): RejectionService {
    if (!RejectionService.instance) {
      RejectionService.instance = new RejectionService();
    }
    return RejectionService.instance;
  }
  
  /**
   * Set the data mode (demo or live)
   */
  setMode(isLive: boolean): void {
    this.isLiveMode = isLive;
  }
  
  /**
   * Record a timesheet rejection
   */
  async recordRejection(
    timesheet: Timesheet,
    rejectionReason: string,
    approverId: string,
    approverName: string,
    projects: Project[],
    costCentres: CostCentre[],
    options?: {
      approverRole?: string;
      affectedEntryIds?: string[]; // If only specific entries are rejected
    }
  ): Promise<RejectionRecord> {
    try {
      // Determine which entries were affected
      const affectedEntries = this.createRejectedEntries(
        timesheet.entries,
        projects,
        costCentres,
        options?.affectedEntryIds
      );
      
      const rejection: RejectionRecord = {
        id: `rejection-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        timesheetId: timesheet.id,
        rejectionDate: new Date(),
        rejectionReason,
        approverId,
        approverName,
        approverRole: options?.approverRole || 'manager',
        affectedEntries,
        status: 'pending_correction',
        employeeNotified: false,
      };
      
      if (this.isLiveMode) {
        // Save to backend API
        await this.saveRejectionToApi(rejection);
      } else {
        // Save to localStorage
        this.saveRejection(rejection);
      }
      
      return rejection;
    } catch (error) {
      console.error('Failed to record rejection:', error);
      throw error;
    }
  }
  
  /**
   * Update rejection status (e.g., when resubmitted or approved)
   */
  updateRejectionStatus(
    rejectionId: string,
    newStatus: RejectionStatus,
    timestamp?: Date
  ): void {
    try {
      const rejections = this.getAllRejections();
      const rejection = rejections.find(r => r.id === rejectionId);
      
      if (!rejection) {
        console.warn(`Rejection ${rejectionId} not found`);
        return;
      }
      
      rejection.status = newStatus;
      
      // Update relevant timestamps
      const now = timestamp || new Date();
      if (newStatus === 'resubmitted') {
        rejection.resubmittedAt = now;
      } else if (newStatus === 'approved_after_correction') {
        rejection.resolvedAt = now;
      }
      
      this.saveAllRejections(rejections);
    } catch (error) {
      console.error('Failed to update rejection status:', error);
    }
  }
  
  /**
   * Get all rejections for a specific timesheet
   */
  getTimesheetRejectionHistory(timesheetId: string): TimesheetRejectionHistory {
    try {
      const rejections = this.getAllRejections()
        .filter(r => r.timesheetId === timesheetId)
        .sort((a, b) => b.rejectionDate.getTime() - a.rejectionDate.getTime());
      
      const currentRejection = rejections.find(
        r => r.status === 'pending_correction' || r.status === 'resubmitted'
      );
      
      return {
        timesheetId,
        rejections,
        currentStatus: currentRejection?.status || null,
        rejectionCount: rejections.length,
      };
    } catch (error) {
      console.error('Failed to get timesheet rejection history:', error);
      return {
        timesheetId,
        rejections: [],
        currentStatus: null,
        rejectionCount: 0,
      };
    }
  }
  
  /**
   * Get rejection summary for an employee
   */
  async getEmployeeRejectionSummary(employeeId: string, timesheets: Timesheet[]): Promise<RejectionSummary> {
    try {
      const employeeTimesheetIds = timesheets
        .filter(ts => ts.employeeId === employeeId)
        .map(ts => ts.id);
      
      let employeeRejections: RejectionRecord[];
      
      if (this.isLiveMode) {
        // Fetch from API
        const apiRejections = await this.fetchAllRejectionsFromApi();
        employeeRejections = apiRejections.filter(r => employeeTimesheetIds.includes(r.timesheetId));
      } else {
        // Use localStorage
        employeeRejections = this.getAllRejections()
          .filter(r => employeeTimesheetIds.includes(r.timesheetId));
      }
      
      const pendingCorrections = employeeRejections.filter(
        r => r.status === 'pending_correction'
      ).length;
      
      const resubmitted = employeeRejections.filter(
        r => r.status === 'resubmitted'
      ).length;
      
      const resolved = employeeRejections.filter(
        r => r.status === 'approved_after_correction'
      ).length;
      
      const lastRejection = employeeRejections
        .sort((a, b) => b.rejectionDate.getTime() - a.rejectionDate.getTime())[0];
      
      return {
        employeeId,
        totalRejections: employeeRejections.length,
        pendingCorrections,
        resubmitted,
        resolved,
        lastRejectionDate: lastRejection?.rejectionDate,
      };
    } catch (error) {
      console.error('Failed to get employee rejection summary:', error);
      return {
        employeeId,
        totalRejections: 0,
        pendingCorrections: 0,
        resubmitted: 0,
        resolved: 0,
      };
    }
  }
  
  /**
   * Fetch all rejection records from API
   */
  private async fetchAllRejectionsFromApi(): Promise<RejectionRecord[]> {
    try {
      const apiRecords = await apiClient.get<ApiRejectionRecord[]>('/rejectionrecords');
      return apiRecords.map(this.convertApiRecordToRejectionRecord);
    } catch (error) {
      console.error('Failed to fetch rejections from API:', error);
      return [];
    }
  }
  
  /**
   * Save rejection record to backend API
   */
  private async saveRejectionToApi(rejection: RejectionRecord): Promise<void> {
    try {
      // Convert to API format (without affectedEntries which backend doesn't store)
      const apiRecord: ApiRejectionRecord = {
        id: rejection.id,
        timesheetId: rejection.timesheetId,
        rejectionDate: rejection.rejectionDate.toISOString(),
        rejectionReason: rejection.rejectionReason,
        approverId: rejection.approverId,
        approverName: rejection.approverName,
        approverRole: rejection.approverRole || 'manager',
        status: rejection.status,
        resubmittedAt: rejection.resubmittedAt?.toISOString() || null,
        resolvedAt: rejection.resolvedAt?.toISOString() || null,
        employeeNotified: rejection.employeeNotified ?? false,
        notificationSentAt: rejection.notificationSentAt?.toISOString() || null,
      };
      
      await apiClient.post('/rejectionrecords', apiRecord);
    } catch (error) {
      console.error('Failed to save rejection to API:', error);
      throw error;
    }
  }
  
  /**
   * Convert API record to frontend RejectionRecord format
   */
  private convertApiRecordToRejectionRecord(apiRecord: ApiRejectionRecord): RejectionRecord {
    return {
      id: apiRecord.id,
      timesheetId: apiRecord.timesheetId,
      rejectionDate: new Date(apiRecord.rejectionDate),
      rejectionReason: apiRecord.rejectionReason,
      approverId: apiRecord.approverId,
      approverName: apiRecord.approverName,
      approverRole: apiRecord.approverRole,
      affectedEntries: [], // Backend doesn't store detailed entry info
      status: apiRecord.status,
      resubmittedAt: apiRecord.resubmittedAt ? new Date(apiRecord.resubmittedAt) : undefined,
      resolvedAt: apiRecord.resolvedAt ? new Date(apiRecord.resolvedAt) : undefined,
      employeeNotified: apiRecord.employeeNotified,
      notificationSentAt: apiRecord.notificationSentAt ? new Date(apiRecord.notificationSentAt) : undefined,
    };
  }
  
  /**
   * Get all timesheets with pending corrections for an employee
   */
  getPendingCorrections(employeeId: string, timesheets: Timesheet[]): Timesheet[] {
    try {
      const employeeTimesheets = timesheets.filter(ts => ts.employeeId === employeeId);
      const rejections = this.getAllRejections();
      
      return employeeTimesheets.filter(ts => {
        const tsRejections = rejections.filter(r => r.timesheetId === ts.id);
        return tsRejections.some(r => r.status === 'pending_correction');
      });
    } catch (error) {
      console.error('Failed to get pending corrections:', error);
      return [];
    }
  }
  
  /**
   * Mark rejection as notified
   */
  markAsNotified(rejectionId: string): void {
    try {
      const rejections = this.getAllRejections();
      const rejection = rejections.find(r => r.id === rejectionId);
      
      if (rejection) {
        rejection.employeeNotified = true;
        rejection.notificationSentAt = new Date();
        this.saveAllRejections(rejections);
      }
    } catch (error) {
      console.error('Failed to mark rejection as notified:', error);
    }
  }
  
  /**
   * Clear all rejections (for testing/development)
   */
  clearAllRejections(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear rejections:', error);
    }
  }
  
  /**
   * Create rejected entry records from timesheet entries
   */
  private createRejectedEntries(
    entries: TimesheetEntry[],
    projects: Project[],
    costCentres: CostCentre[],
    affectedEntryIds?: string[]
  ): RejectedEntry[] {
    const entriesToReject = affectedEntryIds
      ? entries.filter(e => affectedEntryIds.includes(e.id))
      : entries;
    
    return entriesToReject.map(entry => {
      const project = projects.find(p => p.id === entry.projectId);
      const costCentre = costCentres.find(cc => cc.id === entry.costCentreId);
      
      return {
        entryId: entry.id,
        projectId: entry.projectId,
        projectName: project?.name || 'Unknown Project',
        costCentreId: entry.costCentreId,
        costCentreName: costCentre?.name || 'Unknown Cost Centre',
        description: entry.description,
        hours: { ...entry.hours },
      };
    });
  }
  
  /**
   * Save a single rejection
   */
  private saveRejection(rejection: RejectionRecord): void {
    const rejections = this.getAllRejections();
    rejections.push(rejection);
    this.saveAllRejections(rejections);
  }
  
  /**
   * Get all rejections from storage
   */
  private getAllRejections(): RejectionRecord[] {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored);
      return parsed.map((r: any) => ({
        ...r,
        rejectionDate: new Date(r.rejectionDate),
        resubmittedAt: r.resubmittedAt ? new Date(r.resubmittedAt) : undefined,
        resolvedAt: r.resolvedAt ? new Date(r.resolvedAt) : undefined,
        notificationSentAt: r.notificationSentAt ? new Date(r.notificationSentAt) : undefined,
      }));
    } catch (error) {
      console.error('Failed to load rejections:', error);
      return [];
    }
  }
  
  /**
   * Save all rejections to storage
   */
  private saveAllRejections(rejections: RejectionRecord[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(rejections));
    } catch (error) {
      console.error('Failed to save rejections:', error);
      
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        console.warn('localStorage quota exceeded. Consider implementing cleanup.');
      }
    }
  }
}

// Export singleton instance
export const rejectionService = RejectionService.getInstance();
